/**
 * Mobile Menu Toggle Component
 * -----------------------------------------------------------------------------
 * Builds a luxurious mobile drawer whose content is CLONED from the SAME
 * desktop mega-menu source (each top-level item's `.mega-menu-content`, which
 * `menu-item-popover.js` populates from the matching `mega-menu` section).
 *
 * This guarantees the mobile menu always mirrors the desktop menu (single
 * source of truth — edit the mega menu once, both surfaces update) and fixes
 * the previously-broken mobile menu (dead `#mobile-menu` dialog, lazy images
 * inside a `display:none` container, no design-token styling).
 *
 * Styling lives in a scoped `{% stylesheet %}` in `blocks/header-menu.liquid`.
 */

class MobileMenuToggle extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.built = false;
    this.dialog = null;

    this.hamburgerIcon = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" data-slot="icon" aria-hidden="true" class="size-6">
        <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    `;

    this.closeIcon = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" data-slot="icon" aria-hidden="true" class="size-6">
        <path d="M6 18 18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `;
  }

  connectedCallback() {
    this.renderTrigger();
    this.bindTrigger();
  }

  disconnectedCallback() {
    document.body.classList.remove('mm-open');
  }

  /* ----------------------------------------------------------------------- */
  /* Trigger (hamburger)                                                     */
  /* ----------------------------------------------------------------------- */

  renderTrigger() {
    // NOTE: intentionally NO `command`/`commandfor` attributes — we drive the
    // dialog ourselves so the native invoker API can't double-fire showModal().
    this.innerHTML = `
      <div class="flex lg:hidden mobile-menu-toggle__wrap">
        <button type="button"
                class="p-2 bg-white rounded-md text-darkGreen mobile-menu-button"
                aria-label="Open menu"
                aria-haspopup="dialog"
                aria-expanded="false">
          <span class="sr-only">Open menu</span>
          <span class="icon-container">${this.hamburgerIcon}</span>
        </button>
      </div>
    `;
  }

  bindTrigger() {
    const button = this.querySelector('.mobile-menu-button');
    if (button) button.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
  }

  /* ----------------------------------------------------------------------- */
  /* Open / close                                                            */
  /* ----------------------------------------------------------------------- */

  open() {
    if (!this.built) this.build();
    if (!this.dialog) return;

    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.body.classList.add('mm-open');
    this.isOpen = true;
    this.setTriggerState(true);
  }

  close() {
    if (this.dialog) {
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
    }
    document.body.classList.remove('mm-open');
    this.isOpen = false;
    this.setTriggerState(false);
  }

  setTriggerState(open) {
    const button = this.querySelector('.mobile-menu-button');
    const icon = this.querySelector('.icon-container');
    const sr = this.querySelector('.sr-only');
    if (!button) return;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    if (icon) icon.innerHTML = open ? this.closeIcon : this.hamburgerIcon;
    if (sr) sr.textContent = open ? 'Close menu' : 'Open menu';
  }

  /* ----------------------------------------------------------------------- */
  /* Build the drawer (once, lazily on first open)                           */
  /* ----------------------------------------------------------------------- */

  build() {
    this.injectStyles();
    let dialog = document.getElementById('mobile-menu');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'mobile-menu';
      document.body.appendChild(dialog);
    }
    dialog.className = 'mobile-mega-drawer';
    dialog.setAttribute('aria-label', 'Main menu');

    dialog.innerHTML = `
      <div class="mobile-mega-drawer__panel" role="document">
        <div class="mobile-mega-drawer__header">
          ${this.getLogoMarkup()}
          <button type="button" class="mobile-mega-drawer__close" aria-label="Close menu">
            ${this.closeIcon}
          </button>
        </div>
        <nav class="mobile-mega-drawer__nav" aria-label="Mobile">
          <ul class="mobile-mega-drawer__list"></ul>
        </nav>
      </div>
    `;

    const list = dialog.querySelector('.mobile-mega-drawer__list');
    this.getTopLevelItems().forEach((item, i) => {
      const li = this.buildItem(item, i);
      if (li) list.appendChild(li);
    });

    this.wireDialog(dialog);
    this.dialog = dialog;
    this.built = true;
  }

  /**
   * Inject the drawer styles once. Kept in JS (rather than a block stylesheet)
   * so the whole feature ships as a single asset. Plain CSS only — the theme's
   * prebuilt Tailwind lacks arbitrary/responsive variants.
   */
  injectStyles() {
    if (document.getElementById('mobile-mega-drawer-styles')) return;
    const style = document.createElement('style');
    style.id = 'mobile-mega-drawer-styles';
    style.textContent = `
.mobile-mega-drawer{--mm-bg:var(--color-background,#ffffff);--mm-fg:var(--color-foreground,#1c1d1d);--mm-green:#1e4036;--mm-green-accent:#73994a;--mm-border:rgb(0 0 0 / 0.08);--mm-radius:var(--menu-image-border-radius,12px);--mm-pad:20px;--mm-ease:var(--animation-easing,cubic-bezier(0.25,0.1,0.25,1));--mm-speed:320ms;}
body.mm-open{overflow:hidden;}
.menu-section-mobile-component{display:none !important;}
body.mobile-menu-open .menu-section-mobile-component, html body.mm-open .menu-section-mobile-component{display:none !important;}
dialog.mobile-mega-drawer{position:fixed;inset:0 auto 0 0;margin:0;width:min(90vw,430px);max-width:none;height:100dvh;max-height:100dvh;padding:0;border:0;background:var(--mm-bg);color:var(--mm-fg);box-shadow:8px 0 40px rgb(0 0 0 / 0.18);overflow:hidden;transform:translateX(-100%);transition:transform var(--mm-speed) var(--mm-ease),overlay var(--mm-speed) allow-discrete,display var(--mm-speed) allow-discrete;}
dialog.mobile-mega-drawer[open]{transform:translateX(0);}
@starting-style{dialog.mobile-mega-drawer[open]{transform:translateX(-100%);}}
dialog.mobile-mega-drawer::backdrop{background:rgb(0 0 0 / 0.5);backdrop-filter:blur(2px);opacity:0;transition:opacity var(--mm-speed) var(--mm-ease),overlay var(--mm-speed) allow-discrete,display var(--mm-speed) allow-discrete;}
dialog.mobile-mega-drawer[open]::backdrop{opacity:1;}
@starting-style{dialog.mobile-mega-drawer[open]::backdrop{opacity:0;}}
.mobile-mega-drawer__panel{display:flex;flex-direction:column;height:100%;width:100%;background:var(--mm-bg);}
.mobile-mega-drawer__header{flex:0 0 auto;position:sticky;top:0;z-index:2;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px var(--mm-pad);background:var(--mm-bg);border-bottom:1px solid var(--mm-border);}
.mobile-mega-drawer__logo-link{display:inline-flex;align-items:center;}
.mobile-mega-drawer__logo{height:22px;width:auto;display:block;}
.mobile-mega-drawer__logo-text{font-family:var(--font-heading--family,inherit);font-weight:700;letter-spacing:0.12em;color:var(--mm-green);text-decoration:none;}
.mobile-mega-drawer__close{display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;margin:-10px -10px -10px 0;padding:10px;border:0;background:transparent;color:var(--mm-green);cursor:pointer;border-radius:8px;transition:background var(--mm-speed) var(--mm-ease);}
.mobile-mega-drawer__close:hover{background:rgb(0 0 0 / 0.05);}
.mobile-mega-drawer__close svg{width:24px;height:24px;}
.mobile-mega-drawer__nav{flex:1 1 auto;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;}
.mobile-mega-drawer__list{list-style:none;margin:0;padding:0;}
.mobile-mega-drawer__item{border-bottom:1px solid var(--mm-border);}
.mobile-mega-drawer__summary,.mobile-mega-drawer__link{display:flex;align-items:center;justify-content:space-between;gap:12px;min-height:60px;padding:16px var(--mm-pad);font-family:var(--font-heading--family,inherit);font-size:17px;font-weight:600;letter-spacing:0.01em;color:var(--mm-green);text-decoration:none;cursor:pointer;list-style:none;position:relative;z-index:2;background:var(--mm-bg);transition:color var(--mm-speed) var(--mm-ease);}
.mobile-mega-drawer__summary::-webkit-details-marker{display:none;}
.mobile-mega-drawer__summary::marker{content:'';}
.mobile-mega-drawer__link:active,.mobile-mega-drawer__summary:active{color:var(--mm-green-accent);}
.mobile-mega-drawer__chevron{flex:0 0 auto;display:inline-flex;color:var(--mm-green-accent);transition:transform var(--mm-speed) var(--mm-ease);}
.mobile-mega-drawer__chevron svg{width:20px;height:20px;}
details[open] > .mobile-mega-drawer__summary .mobile-mega-drawer__chevron{transform:rotate(180deg);}
.mobile-mega-drawer__content{padding:4px var(--mm-pad) 22px;position:relative;z-index:1;animation:mm-fade var(--mm-speed) var(--mm-ease);}
@keyframes mm-fade{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}
.mobile-mega-drawer__content,.mobile-mega-drawer__content *{max-width:100%;box-sizing:border-box;}
.mobile-mega-drawer__content [class*="h-[var("],.mobile-mega-drawer__content [style*="--group-height"],.mobile-mega-drawer__content [style*="--text-block-height"]{height:auto !important;min-height:0 !important;}
.mobile-mega-drawer__content vertical-tabs-component{display:block !important;}
.mobile-mega-drawer__content .vertical-tabs-content{display:block !important;}
.mobile-mega-drawer__content .vertical-tab-panel{display:block !important;width:100% !important;}
.mobile-mega-drawer__content [role="tablist"],.mobile-mega-drawer__content .vertical-tabs-nav{display:none !important;}
.mobile-mega-drawer__content .product-cards-list-block{display:grid !important;grid-template-columns:repeat(2,minmax(0,1fr)) !important;gap:14px !important;}
.mobile-mega-drawer__content .product-cards-list-block__item{width:auto !important;min-width:0 !important;max-width:100% !important;}
.mobile-mega-drawer__content [class*="grid-block-content"],.mobile-mega-drawer__content [class*="grid-cols-"]{display:grid !important;grid-template-columns:1fr !important;gap:14px !important;}
.mobile-mega-drawer__content [class*="flex-block-content"]{display:flex !important;flex-wrap:wrap !important;gap:14px !important;}
.mobile-mega-drawer__content img{width:100%;height:auto;border-radius:var(--mm-radius);display:block;}
.mobile-mega-drawer__content a{color:var(--mm-fg);text-decoration:none;}
.mobile-mega-drawer__content a:active{color:var(--mm-green-accent);}
@media (prefers-reduced-motion:reduce){dialog.mobile-mega-drawer,dialog.mobile-mega-drawer::backdrop,.mobile-mega-drawer__chevron,.mobile-mega-drawer__content{transition:none;animation:none;}}
`;
    document.head.appendChild(style);
  }

  getLogoMarkup() {
    const logo = document.querySelector('#header-group .header-logo img, #header-group a[href="/"] img, .header-logo img');
    if (logo) {
      const clone = logo.cloneNode(true);
      clone.removeAttribute('loading');
      clone.className = 'mobile-mega-drawer__logo';
      return `<a href="/" class="mobile-mega-drawer__logo-link" aria-label="Home">${clone.outerHTML}</a>`;
    }
    return `<a href="/" class="mobile-mega-drawer__logo-link mobile-mega-drawer__logo-text" aria-label="Home">PROLON</a>`;
  }

  /**
   * Top-level desktop items live in `blocks/header-menu.liquid`'s `.hidden lg:!flex`
   * row; each `menu-item` wraps its trigger in `.item-wrapper-menu`. The misnamed
   * `single-mobile-menu-dialog` el-popovers ("BACK") lack `.item-wrapper-menu`, so
   * keying off it cleanly excludes them.
   */
  getTopLevelItems() {
    const seen = new Set();
    const items = [];
    document.querySelectorAll('.item-wrapper-menu').forEach((wrap) => {
      const root = wrap.closest('.single-mega-menu');
      if (root && !seen.has(root)) { seen.add(root); items.push(root); }
    });
    return items;
  }

  buildItem(root, index) {
    const trigger = root.querySelector('.item-wrapper-menu [popovertarget]');
    const simpleLink = root.querySelector('.item-wrapper-menu a[href]');
    const isMega = root.getAttribute('data-mega-menu') === 'true';
    const content = root.querySelector('.mega-menu-content');

    const li = document.createElement('li');
    li.className = 'mobile-mega-drawer__item';

    // Simple (non-mega) link
    if (!isMega || !content || !content.children.length) {
      const href = simpleLink ? simpleLink.getAttribute('href') : '#';
      const title = this.titleOf(trigger, simpleLink);
      li.innerHTML = `<a class="mobile-mega-drawer__link" href="${href}">${title}</a>`;
      const a = li.querySelector('a');
      if (a) a.addEventListener('click', () => this.close());
      return li;
    }

    // Mega item → accordion that reuses the desktop content
    const title = this.titleOf(trigger, simpleLink);
    const details = document.createElement('details');
    details.className = 'mobile-mega-drawer__accordion';

    const summary = document.createElement('summary');
    summary.className = 'mobile-mega-drawer__summary';
    summary.innerHTML = `
      <span class="mobile-mega-drawer__summary-text">${title}</span>
      <span class="mobile-mega-drawer__chevron" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="currentColor" class="size-5">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" />
        </svg>
      </span>`;

    const body = document.createElement('div');
    body.className = 'mobile-mega-drawer__content mega-menu-content';
    body.appendChild(this.cloneContent(content));

    details.appendChild(summary);
    details.appendChild(body);
    li.appendChild(details);

    body.querySelectorAll('a[href]').forEach((a) => a.addEventListener('click', () => this.close()));
    return li;
  }

  titleOf(trigger, simpleLink) {
    if (trigger) {
      const label = (trigger.getAttribute('aria-label') || '').replace(/menu,\s*click to expand/i, '').trim();
      if (label) return label;
      const txt = (trigger.textContent || '').trim();
      if (txt) return txt;
    }
    if (simpleLink) return (simpleLink.textContent || '').trim();
    return 'Menu';
  }

  /**
   * Deep-clone the desktop mega content, then make it mobile-safe:
   *  - strip ids (avoid duplicate-id collisions with the live desktop popover)
   *  - force every image to load eagerly (the original bug: lazy images never
   *    entered the viewport inside a hidden container)
   *  - hydrate any data-src / data-srcset deferred-loading hooks
   */
  cloneContent(source) {
    const clone = source.cloneNode(true);

    // Defuse cloned custom elements (e.g. <vertical-tabs-component>) so they do
    // NOT re-run their JS when inserted — re-init throws (it expects ids we strip)
    // and the drawer already flattens tabs via CSS, so no behaviour is lost.
    // Replace each defined custom element with a plain <div>, preserving its
    // classes/attributes and children. Loop handles nested custom elements.
    let guard = 0;
    while (guard++ < 100) {
      const all = clone.querySelectorAll('*');
      let target = null;
      for (const el of all) {
        const tag = el.tagName.toLowerCase();
        if (tag.includes('-') && customElements.get(tag)) { target = el; break; }
      }
      if (!target) break;
      const div = document.createElement('div');
      for (const attr of target.attributes) div.setAttribute(attr.name, attr.value);
      while (target.firstChild) div.appendChild(target.firstChild);
      target.replaceWith(div);
    }

    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

    clone.querySelectorAll('img').forEach((img) => {
      img.setAttribute('loading', 'eager');
      img.removeAttribute('fetchpriority');
      const ds = img.getAttribute('data-src');
      const dss = img.getAttribute('data-srcset');
      if (ds && !img.getAttribute('src')) img.setAttribute('src', ds);
      if (dss && !img.getAttribute('srcset')) img.setAttribute('srcset', dss);
      img.classList.remove('lazyload', 'lazyloading');
    });

    // Reveal lazy <source> elements in <picture>
    clone.querySelectorAll('source[data-srcset]').forEach((s) => {
      if (!s.getAttribute('srcset')) s.setAttribute('srcset', s.getAttribute('data-srcset'));
    });

    return clone;
  }

  /* ----------------------------------------------------------------------- */
  /* Dialog wiring                                                           */
  /* ----------------------------------------------------------------------- */

  wireDialog(dialog) {
    const closeBtn = dialog.querySelector('.mobile-mega-drawer__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    // Native dialog close (Esc / .close()) → sync trigger + body class
    dialog.addEventListener('close', () => {
      document.body.classList.remove('mm-open');
      this.isOpen = false;
      this.setTriggerState(false);
    });
    dialog.addEventListener('cancel', () => this.close());

    // Click on the backdrop (the dialog element itself, outside the panel) closes
    dialog.addEventListener('click', (e) => {
      const panel = dialog.querySelector('.mobile-mega-drawer__panel');
      if (panel && !panel.contains(e.target)) this.close();
    });
  }
}

if (!customElements.get('mobile-menu-toggle')) {
  customElements.define('mobile-menu-toggle', MobileMenuToggle);
}
